import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import './index.less'
import { Button, Drawer, Form, message, Tooltip } from 'antd'
import { ExclamationCircleOutlined, LeftOutlined, SyncOutlined } from '@ant-design/icons'
import RowHeader from '../RowHeader'
import ExperEnv from '../ExperEnv'
import Flex from '../Flex'
import { getExperEnv, getExperEnvAll } from '@/api/modules/cli'
import SharedDataContext from '@/utils/share'
import ExperPorts from '../ExperPorts'
import ExperEnvCenter from '../ExperEnvCenter'
import { changeCourseTemplate, copyTemplateEnv, verifyResources } from '@/api/modules/community'
import { courseTypes } from '@/utils/commonTypes'
import Container from './Container'
import { rename } from '@/api/modules/fs'
import { name } from 'pubsub-js'
import useResourcesLimit from '@/hooks/useResourcesLimit'

type ExperEnvManageProps = {
    open: boolean
    onClose: () => void
    courseType: any
}

// 步骤枚举类
// base 主页面   env 选择环境页面  port  添加端口
export enum StepEnum {
    BASE = 'BASE',
    ENV = 'ENV',
    PORT = 'PORT'
}
// 步骤类型
export type stepType = keyof typeof StepEnum

const ExperEnvManage: React.FC<ExperEnvManageProps> = (props) => {

    const { open, onClose, courseType } = props

    // 获取共享数据
    const { section, setSection, labCode, couseCode, userInfo, selectedChapter, params, repoEnvStatus, courseDetail } = useContext(SharedDataContext)
    const [form] = Form.useForm()
    const [step, setStep] = useState<stepType>(StepEnum.BASE)                         // 当前更新步骤
    const [loading, setLoading] = useState(false)                                     // 保存loading
    const [envRefresh, setEnvRefresh] = useState(false)                               // 选择环境刷新按钮控制
    const [formData, setFormData] = useState<any>()                                   // 临时保存的数据
    const [experEnvList, setExperEnvList] = useState<any[]>([])                       // 环境列表
    const [initPort, setInitPort] = useState<any>()                                   // 初始化端口
    const hander = useRef<any>()
    const handleResourseError = useResourcesLimit()

    const template = Form.useWatch('template', form)

    useEffect(() => {
        if (!open) {
            setStep(StepEnum.BASE)
            setFormData({})
            return
        }
        if (open) {
            fetchExperEnvList()
            let template: any = {}
            // 课程的环境修改不走继承
            if (courseType !== courseTypes.COURSE) {
                if (section?.template && Object.keys(section?.template)?.length) {
                    template = section?.template
                } else if (repoEnvStatus?.template && Object.keys(repoEnvStatus?.template)?.length) {
                    template = repoEnvStatus.template
                }
            } else {
                template = selectedChapter?.describe?.template
            }
            form.setFieldsValue({
                experEnv: template?.experEnv,
                expertools: template?.expertools,
                ports: template?.ports ?? [],
                duration: template?.duration ?? 30,
                name: template?.name,
            })
        }
    }, [open, section, selectedChapter, courseType])

    // 计算标题
    const title = useMemo(() => {
        let title
        if (step === StepEnum.BASE) {
            title = '实验环境管理'
        } else {
            title = step === StepEnum.ENV ? '选择环境' : `${initPort ? '编辑' : '添加'}端口`
        }
        return title!
    }, [step, initPort])

    // 上一步或取消
    const prev = (cancel: boolean) => {
        if (cancel) {
            onClose()
            return
        }
        changeStep(StepEnum.BASE)
        setInitPort(null)
    }

    // 保存退出弹框  保存环境  保存端口设置
    const save = async () => {
        const data = await form.validateFields().catch(() => { })
        if (data == undefined) return
        setLoading(true)
        if (step === StepEnum.BASE) {
            await saveExit(data)
        } else if (step === StepEnum.ENV) {
            await saveEnv(data)
        } else if (step === StepEnum.PORT) {
            await savePort(data)
        }
        setLoading(false)
    }
    // 保存退出
    const saveExit = async (data: any) => {
        // 如何环境存在先校验环境时长，判断是否超出限制
        if (data?.experEnv) {
            const can = await verifyResources({
                labCode,
                data: { duration: data.duration }, quotaType: "CODE",
                quotaValue: "ENV_CONFIG_DURATION_LIMIT"
            }).catch((err) => {
                if (handleResourseError(err)) return
                message.destroy()
                message.error(err?.message ?? '校验失败')
            })
            if (can == undefined) return
        }
        // 环境是否需要先拷贝到实验室
        if (formData?.template?.location === data?.experEnv && formData?.template?.lab) {
            const res = await copyTemplateEnv({
                envId: formData?.template?.id,
                envLab: formData?.template?.lab,
                newLab: labCode,
            }).catch((err) => {
                if (handleResourseError(err)) return
                message.destroy()
                message.error(err?.message ?? '保存失败')
            })
            if (res == undefined) return
            data.experEnv = res?.location
        }
        // 对数据进行处理
        const _data = { ...selectedChapter }
        const ports = data?.ports?.map((item: any) => {
            const type = item?.type ?? 'system'
            return { ...item, type }
        })
        const portFind = ports?.find((i: any) => i.name === selectedChapter?.describe?.template?.defaultOpenName && i.port === selectedChapter?.describe?.template?.defaultOpen)

        let template
        if (portFind >= 0) {
            template = {
                ...selectedChapter?.describe?.template,
                experEnv: data?.experEnv,
                expertools: data?.expertools,
                ports,
                duration: data?.duration,
                name: data?.name,
            }
        } else {
            template = {
                ...selectedChapter?.describe?.template,
                experEnv: data?.experEnv,
                expertools: data?.expertools,
                ports,
                duration: data?.duration,
                name: data?.name,
                defaultOpenName: ports?.find((i: any) => i.isDefaultTool)?.name,
                defaultOpen: ports?.find((i: any) => i.isDefaultTool)?.port,
            }
        }

        _data.describe.template = template
        // 修改小节的环境
        const res = await rename({
            repo: params?.get('repo'),
            key: params?.get('key'),
            data: {
                title: _data?.title.trim(),
                path: _data?.path,
                parent: _data?.parent,
                describe: _data?.describe ?? {}
            }
        }).catch((err) => {
            if (handleResourseError(err)) return
            message.destroy()
            message.warning(err?.message ?? '保存失败')
        })
        if (res == undefined) return
        if (courseType !== courseTypes.COURSE) {
            // 修改课程的环境
            const code = await changeCourseTemplate(couseCode, JSON.stringify(template)).catch((err) => {
                if (handleResourseError(err)) return
                message.destroy()
                message.warning('保存失败')
            })
        }
        message.destroy()
        message.success('保存成功')
        onClose?.()
        setSection({ ...section, template })
    }
    // 保存环境
    const saveEnv = async (data: any) => {
        const { template } = data
        if (template && template?.id !== 'default-template-id') {
            setExperEnvList([...experEnvList, template])
            setFormData({
                ...formData,
                template,
                experEnv: template?.location,
                expertools: template?.containers?.[0]?.name,
                ports: template?.containers?.[0]?.ports?.map((item: any) => ({ ...item, choice: true })) ?? [],
                name: template?.name
            })
        } else {
            setFormData({
                ...formData,
                template,
                experEnv: undefined,
                expertools: undefined,
                ports: [],
                name: ''
            })
        }
        changeStep(StepEnum.BASE)
    }
    // 保存端口
    const savePort = async (data: any) => {
        data.port = Number(data?.port)
        const port = data?.port
        if (formData?.ports?.some((item: any) => item?.port !== initPort?.port && item?.port === port)) {
            message.destroy()
            message.warning('端口号已存在，请重新输入')
            return
        }
        if (initPort) {
            const res = { ...initPort, ...data }
            const ports = formData?.ports?.map((item: any) => {
                if (item?.port === initPort?.port) {
                    return { ...item, ...res }
                }
                return { ...item }
            })
            setFormData({ ...formData, ports })
        } else {
            const port = {
                ...data,
                choice: true,
                type: 'creator'
            }
            const ports = formData?.ports?.concat([port]) ?? [port]
            setFormData({ ...formData, ports })
        }
        changeStep(StepEnum.BASE)
        setInitPort(null)
    }

    // 改变当前步骤
    const changeStep = (next: StepEnum) => {
        // 跳转到其他步骤优先保存
        if (step === StepEnum.BASE) {
            const data = form.getFieldsValue()
            setFormData({ ...formData, ...data })
        }
        setStep(next)
    }

    // 获取环境列表
    const fetchExperEnvList = async () => {
        const res = await getExperEnvAll({ owner: userInfo?.userId, lab: labCode }).catch(() => { })
        setExperEnvList(res ?? [])
    }

    // 更新端口
    const updatePort = (type: 'update' | 'add', ports: any, value?: any) => {
        if (type === 'update') {
            setInitPort(value)
        }
        changeStep(StepEnum.PORT)
    }


    return (
        <Drawer
            placement='right'
            closable={false}
            destroyOnClose
            onClose={onClose}
            open={open}
            width={712}
            maskClosable={false}
            rootClassName='ExperEnvManage'
        >
            <Flex justify='space-between' align='center' style={{ height: '48px', borderBottom: '1px solid #E5E5E5', padding: '0 30px' }}>
                <span>
                    {
                        step !== StepEnum.BASE && <LeftOutlined onClick={() => { changeStep(StepEnum.BASE) }} style={{ color: '#999999', fontSize: '16px' }} />
                    }
                </span>
                <span style={{ color: '#333333', fontSize: '16px', fontWeight: 'bold' }}>{title}</span>
                <span>
                    {
                        step === StepEnum.ENV && <Tooltip
                            placement="bottomLeft"
                            title={'刷新实验环境'}
                            arrow={false}
                            color='white'
                            overlayClassName='custom-tooltip'
                        >
                            <SyncOutlined className='ExperEnvManage-refresh' spin={envRefresh} style={{ color: '#999999', fontSize: '16px' }} onClick={async () => {
                                setEnvRefresh(true)
                                await hander.current?.reFresh().finally(() => {
                                    setEnvRefresh(false)
                                    message.success('刷新成功')
                                })
                            }} />
                        </Tooltip>
                    }
                </span>
            </Flex>

            <div className='DirectoryDrawer-content'>
                <Form
                    form={form}
                    colon={false}
                    autoComplete="off"
                    style={{ height: '100%' }}
                >
                    {
                        step === StepEnum.BASE ? <Container labCode={labCode} disabledUpdate={courseDetail?.labType !== 'EDUCATION'} changeStep={changeStep} formData={formData} experEnvList={experEnvList} updatePort={updatePort} /> :
                            step === StepEnum.ENV ? <ExperEnvCenter ref={hander} labCode={labCode} initTemplate={formData?.experEnv} /> :
                                step === StepEnum.PORT ? <ExperPorts initValue={initPort} /> :
                                    <></>
                    }
                </Form>
            </div>

            <Flex gap={20} justify='center' align='center' style={{ height: '72px' }}>
                <Button onClick={() => { prev(step === StepEnum.BASE) }}>{step === StepEnum.BASE ? '取消' : '上一步'}</Button>
                <Button loading={loading} disabled={step === StepEnum.ENV && !template} type='primary' onClick={save}>保存</Button>
            </Flex>
        </Drawer>
    )
}

export default ExperEnvManage
